import React from "react";
import RuleBodyPage from "components/common/RuleBodyPage";
import { RuleModalBodyProps } from "components/common/RuleModal";
import { useTranslation } from "react-i18next";

/**
 * Quiz 게임 규칙 설명
 */
const QuizRuleBody: React.FC<RuleModalBodyProps> = ({ page }) => {
  const { t } = useTranslation();

  const phaseContainer =
    "flex items-center gap-3 p-3 bg-gray-800 rounded-lg mb-4";
  const iconContainer =
    "w-8 h-8 min-w-8 min-h-8 flex items-center justify-center rounded-full text-sm";

  return (
    <div className="relative flex">
      {/* Page 1: 게임 개요 */}
      <RuleBodyPage show={page === 0}>
        <div className="space-y-4">
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-blue-600`}>
              <i className="fas fa-user-tie" />
            </div>
            <div>
              <div className="font-medium text-blue-300 mb-1">
                {t("Quiz.rules.hostRole")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.hostRoleDesc")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-green-600`}>
              <i className="fas fa-users" />
            </div>
            <div>
              <div className="font-medium text-green-300 mb-1">
                {t("Quiz.rules.playerRole")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.playerRoleDesc")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-purple-600`}>
              <i className="fas fa-gamepad" />
            </div>
            <div>
              <div className="font-medium text-purple-300 mb-1">
                {t("Quiz.rules.gameFlow")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.gameFlowDesc")}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 2: 플레이 방법 */}
      <RuleBodyPage show={page === 1}>
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="font-medium text-green-300 mb-2">
              🎮 {t("Quiz.rules.howToPlay")}
            </div>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold min-w-6 flex-shrink-0">
                  1.
                </span>
                <span>{t("Quiz.rules.step1")}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold min-w-6 flex-shrink-0">
                  2.
                </span>
                <span>{t("Quiz.rules.step2")}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold min-w-6 flex-shrink-0">
                  3.
                </span>
                <span>{t("Quiz.rules.step3")}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-bold min-w-6 flex-shrink-0">
                  4.
                </span>
                <div>
                  <div>{t("Quiz.rules.step4")}</div>
                  <div className="mt-2 ml-2 space-y-1 text-gray-300">
                    <div className="flex items-start gap-2">
                      <span className="min-w-8">
                        {t("Quiz.rules.step4Option1")}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="min-w-8">
                        {t("Quiz.rules.step4Option2")}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="min-w-8">
                        {t("Quiz.rules.step4Option3")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>
      {/* Page 3: 부저 모드 */}
      <RuleBodyPage show={page === 2}>
        <div className="space-y-4">
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-red-600`}>
              <i className="fas fa-bell" />
            </div>
            <div>
              <div className="font-medium text-red-300 mb-1">
                {t("Quiz.rules.buzzerMode")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.buzzerModeDesc")}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-yellow-600`}>
              <i className="fas fa-check-circle" />
            </div>
            <div>
              <div className="font-medium text-yellow-300 mb-1">
                {t("Quiz.rules.oxMode")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.oxModeDesc")}
              </div>
            </div>
          </div>
          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-indigo-600`}>
              <i className="fas fa-edit" />
            </div>
            <div>
              <div className="font-medium text-indigo-300 mb-1">
                {t("Quiz.rules.answerMode")}
              </div>
              <div className="text-sm text-gray-300">
                {t("Quiz.rules.answerModeDesc")}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      <RuleBodyPage show={page === 3}>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="font-medium text-blue-300 mb-2">
            💡 {t("Quiz.rules.tips")}
          </div>
          <ul className="list-inside text-sm text-gray-300">
            <li>{t("Quiz.rules.tip1")}</li>
            <li>{t("Quiz.rules.tip2")}</li>
            <li>{t("Quiz.rules.tip3")}</li>
          </ul>
        </div>
      </RuleBodyPage>
    </div>
  );
};

export default QuizRuleBody;
